// Helpers for accepting a TOTP / 2FA secret in two shapes:
//   - raw base32 secret (with or without spaces — authenticator apps
//     display "JBSW Y3DP EHPK 3PXP")
//   - otpauth://totp/...?secret=BASE32 URI (what jsQR yields from a QR
//     code image)
//
// Mirrors backend pkg/lib/webauth/steps/browser/totp.go extractBase32Secret.
// We normalize at submit time so the credential blob stores a single
// canonical shape; the backend re-normalizes on read for defense in depth.

import jsQR from 'jsqr';

// Canonical key the recipe runtime reads at replay time.
// Must match backend browser.TOTPSecretInputKey.
export const TOTP_SECRET_INPUT_KEY = 'totp_secret';

export interface NormalizedTOTP {
  secret: string; // canonical no-spaces base32
  source: 'base32' | 'otpauth';
}

export function normalizeTOTPSecret(input: string): NormalizedTOTP {
  const trimmed = input.trim();
  if (trimmed.startsWith('otpauth://')) {
    const url = new URL(trimmed);
    const secret = url.searchParams.get('secret');
    if (!secret) {
      throw new Error("otpauth URI missing 'secret' parameter");
    }
    return { secret: secret.replace(/\s+/g, ''), source: 'otpauth' };
  }
  return { secret: trimmed.replace(/\s+/g, ''), source: 'base32' };
}

// decodeQRImage reads a user-selected image file and runs jsQR on its
// pixels. Returns the decoded payload (typically an otpauth:// URI for
// 2FA QR codes). Throws when no QR code is found or the file isn't a
// readable image.
export async function decodeQRImage(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('failed to get 2D canvas context');
    ctx.drawImage(bitmap, 0, 0);
    const pixels = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    const code = jsQR(pixels.data, pixels.width, pixels.height);
    if (!code) throw new Error('no QR code found in image');
    return code.data;
  } finally {
    bitmap.close();
  }
}
